import { GraphQLClient } from "graphql-request"
import { getSiteByLocale } from "services/sites"
import { convertFlatMenuToStructuredTree } from "util/helpers"

// GraphQL queries
import aiGlobalsQuery from "./queries/aiGlobals"
import siteQuery from "./queries/siteData"
import siteQueryWithChannels from "./queries/siteDataWithChannels"
import meetingStart from "./queries/meetingStart"
import homepageQuery from "./queries/homepage"
import loginRegisterQuery from "./queries/loginRegister"
import atAGlanceQuery from "./queries/atAGlance"
import facultyIndexQuery from "./queries/facultyIndex"
import programGuideQuery from "./queries/programGuide"
import pageQuery from "./queries/page"
import registrationPage from "./queries/registrationPage"
import webviewsQuery from "./queries/webviews"

export async function getSiteData(craftSiteId, withChannels = false) {
    const query = withChannels ? siteQueryWithChannels : siteQuery

    const siteData = await cmsRequest(query, { siteId: craftSiteId })
    // build menu hierarchy here
    siteData.menu = convertFlatMenuToStructuredTree(siteData.flatmenu)

    if (withChannels) {
        // add htmlTitle field that replaces hyphens with non-breaking hyphens
        siteData.channels.map(channel => {
            channel.htmlTitle = channel.title.replace(/-/gi, "&#8209;")
        })
    }

    return siteData
}

export async function getAiGlobals(craftSiteId) {
    const data = await cmsRequest(aiGlobalsQuery, { siteId: craftSiteId })
    return data?.ai ?? null
}

export async function getSiteGlobals(craftSiteId) {
    return await cmsRequest(webviewsQuery, { siteId: craftSiteId })
}

export async function getMeetingStart(craftSiteId) {
    return await cmsRequest(meetingStart, { siteId: craftSiteId })
}

export function getSingle(craftSiteId, section, draftId = null, revisionId = null, previewToken = null) {
    let variables = {
        siteId: craftSiteId,
        draftId: draftId,
        revisionId: revisionId,
    }

    const queries = {
        programAtAGlance: atAGlanceQuery,
        facultyIndex: facultyIndexQuery,
        homePage: homepageQuery,
        loginRegister: loginRegisterQuery,
        programGuide: programGuideQuery,
    }

    return cmsRequest(queries[section], variables, previewToken)
}

export function getPage(craftSiteId, slug, draftId = null, revisionId = null, previewToken = null, type = "default") {
    // TODO: guard pages that require a session

    let variables = {
        siteId: craftSiteId,
        slug: slug,
        draftId: draftId,
        revisionId: revisionId,
        status: ["live", "disabled"],
    }

    let query = null

    // type = Craft section
    switch (type) {
        case "default":
            query = pageQuery
            break
        case "registration":
            query = registrationPage
            break
        case "loginRegister":
            query = loginRegisterQuery
            break
        case "homePage":
            query = homepageQuery
            break
        case "facultyIndex":
            query = facultyIndexQuery
            break
        case "programGuide":
            query = programGuideQuery
            break
        case "programAtAGlance":
            query = atAGlanceQuery
            break
        default:
            query = pageQuery
    }

    return cmsRequest(query, variables, previewToken)
}

export const cmsRequest = async (query, variables, previewToken) => {
    // TODO: add isLoggedIn to all API calls ??

    let endpoint = process.env.CRAFT_GQL_URL || process.env.NEXT_PUBLIC_CRAFT_GQL_URL

    // enable previewing drafts and Craft's live preview feature
    if (previewToken) {
        endpoint += "?token=" + previewToken
    }

    const client = new GraphQLClient(endpoint)

    return await client.request(query, variables).then(data => {
        return data
    })
}

export async function getPageData(options) {
    const site = getSiteByLocale(options.locale)

    const requestPromises = [getSiteData(site.craftId, true)]

    // get all possible alt version query params
    const alternateVersionParams = options.query
        ? [
              options.query.draftId ? parseInt(options.query.draftId, 10) : null,
              options.query.revisionId ? parseInt(options.query.revisionId, 10) : null,
              options.query["x-craft-preview"] || options.query["x-craft-live-preview"] ? options.query.token : null,
          ]
        : [null, null, null]

    if (options.page) {
        requestPromises.push(
            getPage(
                site.craftId,
                options.page.slug,
                ...alternateVersionParams,
                options.page.type || "default", // or registration
            ),
        )
    }

    if (options.single) {
        requestPromises.push(getSingle(site.craftId, options.single.handle, ...alternateVersionParams))
    }

    return await Promise.all(requestPromises).then(result => {
        const data = {
            site: result[0],
        }

        // set the timezone, and guard against a site with the meeting globals not defined
        // emmaMeetingId is always overridden from config to ensure single source of truth
        data.site.meeting = {
            ...(data.site.meeting || {}),
            timezone: site.timezone,
            emmaMeetingId: site.emmaId ?? null,
        }

        if (options.page || options.single) {
            data.page = result[1].data
        }

        return data
    })
}

